import React from 'react';
import { FaFilePdf } from 'react-icons/fa';
import { analysisBtnStyle, buildAnalysisReportHtml, replaceNotesTextareas } from '../reportUtils';

// Renders below an epic analysis reply and downloads it as a multi-page PDF.
export const ExportPdfButton = ({ contentRef }: { contentRef: React.RefObject<HTMLDivElement> }) => {
  const [busy, setBusy] = React.useState(false);

  const handleExport = async () => {
    const node = contentRef.current;
    if (!node || busy) return;
    setBusy(true);

    // Render an off-screen, print-styled copy so the PDF layout is independent of
    // the chat bubble styling. node.innerHTML is React-escaped output (safe).
    const sanitized = node.cloneNode(true) as HTMLElement;
    sanitized.querySelectorAll('.rcb-row-actions').forEach(el => el.remove());
    replaceNotesTextareas(sanitized, node);
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-10000px';
    container.style.top = '0';
    container.style.background = '#ffffff';
    container.innerHTML = buildAnalysisReportHtml(sanitized.innerHTML, new Date().toLocaleString());
    document.body.appendChild(container);

    try {
      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import('jspdf'),
        import('html2canvas'),
      ]);

      const target = container.querySelector('.rpt') as HTMLElement;
      const canvas = await html2canvas(target, { scale: 2, backgroundColor: '#ffffff', useCORS: true });

      const pdf = new jsPDF('l', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const imgData = canvas.toDataURL('image/png');

      let heightLeft = imgHeight;
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      while (heightLeft > 0) {
        position -= pageHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`epic-coverage-analysis-${Date.now()}.pdf`);
    } catch (err) {
      console.error('Failed to export PDF:', err);
    } finally {
      document.body.removeChild(container);
      setBusy(false);
    }
  };

  return (
    <button
      onClick={handleExport}
      disabled={busy}
      title="Download this analysis as a PDF"
      style={analysisBtnStyle(busy)}
    >
      <FaFilePdf />
      {busy ? 'Preparing PDF…' : 'Export to PDF'}
    </button>
  );
};
